import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { FaArrowDown, FaArrowUp, FaPencilAlt } from 'react-icons/fa'
import { IoIosRepeat } from 'react-icons/io'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent'
import NoRecordFound from '../../../components/noRecord/NoRecordFound'
import Pagination from '../../../components/pagination/Pagination'
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent'
import SearchSelect from '../../../components/textFields/SearchSelect'
import TextField from '../../../components/textFields/TextField'
import DateRangeComp from '../../../components/dateComponent/DateRangeComp'
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu'
import { useGetAllUserDraftQuery } from '../../../services/DraftApi'
import { useGetAllDivisionQuery, useDepartmentsLookupQuery, useGetAllDesignationWithoutRoleQuery, useGetAllLocationsQuery } from '../../../services/GeneralApi'
import labelKey from "../../localization/label.json"
import UpdateUserStatusModal from './UpdateUserStatusModal'
import OnboardingUserRequestDetailModal from './OnboardingUserRequestDetailModal'
import OnboardingUserRequestEditModal from './OnboardingUserRequestEditModal'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import { FaCheck, FaEye } from 'react-icons/fa6'
import { IoMdClose } from 'react-icons/io'
import { Link } from 'react-router-dom'
import { constraintConfig } from '../../../constraintConfig'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'
import { KTSVG } from '../../../_metronic/helpers'
import ReactReadMoreReadLess from "react-read-more-read-less"
import useColumnVisibility from '../../customHooks/useColumnVisibility'

const CC_REQUEST_OPTIONS = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '2', label: 'No' },
]

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: constraintConfig.newUserRequestStatus.Pending, label: labelKey.pendingForApproval },
    { value: constraintConfig.newUserRequestStatus.Approved, label: 'Approved' },
    { value: constraintConfig.newUserRequestStatus.Rejected, label: 'Rejected' },
]

const lookupOptionLabel = (options: { value: string; label: string }[], id: any, fallbackId?: any) => {
    const raw = id != null && id !== '' ? id : fallbackId
    if (raw == null || raw === '' || Number(raw) === 0) return 'N/A'
    const key = String(raw)
    return options.find(o => o.value === key)?.label || key
}

const displayManager = (row: any) => {
    const name = row?.managerName?.trim()
    const email = row?.managerEmail?.trim()
    if (name && email) return `${name} (${email})`
    if (name) return name
    if (email) return email
    return 'N/A'
}

const displayPhone = (row: any) => {
    const phone = (row?.cellPhone || row?.businessPhone || '').toString().trim()
    if (!phone || /^[\s_\-]+$/.test(phone)) return 'N/A'
    return phone
}

const AccountManager = ({ tabChange }: any) => {
    const { roleID, isTravelModuleAccessAvailable } = useAppSelector(selectAuth)
    const isOrgAdmin = roleID === constraintConfig.roleID.role2
        || roleID === constraintConfig.roleID.role1
        || roleID === constraintConfig.roleID.roleIDAdmin

    const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'onboardingUserRequests' })
    const { data: divisionLookup } = useGetAllDivisionQuery('')
    const { data: departmentLookup } = useDepartmentsLookupQuery('')
    const { data: designationLookup } = useGetAllDesignationWithoutRoleQuery('')
    const { data: locationsData } = useGetAllLocationsQuery('')

    const COLUMN_IDS = useMemo(() => ([
        'sr', 'firstName', 'managerEmail', 'cellPhone', 'division', 'department', 'designation', 'dayforceId',
        'findAddress', 'location', 'apiOrgName', 'status', 'comments',
        'createdDT', 'modifiedOn', 'approvelDT',
        ...(isTravelModuleAccessAvailable ? ['requestTravelPortalAccess'] : []),
        'requestCorporateCreaditCard', 'spendingLimit', 'actions',
    ]), [isTravelModuleAccessAvailable])

    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [searchStr, setSearchStr] = useState('')
    const [sortBy, setSortBy] = useState('createdDT')
    const [sortOrder, setSortOrder] = useState('desc')
    const [sortedColumn, setSortedColumn] = useState('createdDT')
    const [filterByStatus, setFilterByStatus] = useState(constraintConfig.newUserRequestStatus.Pending)
    const [filterByCcRequest, setFilterByCcRequest] = useState('0')
    const [filterByUserName, setFilterByUserName] = useState('')
    const [filterByEmail, setFilterByEmail] = useState('')
    const [filterByOrgName, setFilterByOrgName] = useState('')
    const [filterByDivisionId, setFilterByDivisionId] = useState('')
    const [filterByDepartmentId, setFilterByDepartmentId] = useState('')
    const [filterByLocationId, setFilterByLocationId] = useState('')
    const [filterByPhone, setFilterByPhone] = useState('')
    const [requestedFrom, setRequestedFrom] = useState('')
    const [requestedTo, setRequestedTo] = useState('')
    const [modifiedFrom, setModifiedFrom] = useState('')
    const [modifiedTo, setModifiedTo] = useState('')
    const [approvedFrom, setApprovedFrom] = useState('')
    const [approvedTo, setApprovedTo] = useState('')
    const [activeTab, setActiveTab] = useState(constraintConfig.newUserRequestStatus.Pending)

    const divisionOptions = useMemo(() => [
        { value: '', label: 'All Divisions' },
        ...((divisionLookup?.result || []).map((d: any) => ({
            value: String(d.id),
            label: d.value || d.title || String(d.id),
        })))
    ], [divisionLookup])

    const departmentOptions = useMemo(() => [
        { value: '', label: 'All Departments' },
        ...((departmentLookup?.result || []).map((d: any) => ({
            value: String(d.id),
            label: d.value || d.title || String(d.id),
        })))
    ], [departmentLookup])

    const designationOptions = useMemo(() => [
        { value: '', label: 'All Designations' },
        ...((designationLookup?.result || []).map((d: any) => ({
            value: String(d.id),
            label: d.value || d.title || String(d.id),
        })))
    ], [designationLookup])

    const locationOptions = useMemo(() => [
        { value: '', label: 'All Locations' },
        ...((locationsData?.result || []).map((d: any) => ({
            value: String(d.id),
            label: d.value || d.title || String(d.id),
        })))
    ], [locationsData])

    const { data, isFetching: isLoading, isSuccess, refetch } = useGetAllUserDraftQuery({
        pageNumber: page,
        pageSize,
        sortBy,
        sortOrder,
        searchStr,
        filterByStatus: filterByStatus || 0,
        filterByCcRequest: Number(filterByCcRequest) || 0,
        filterByUserName: filterByUserName || undefined,
        filterByEmail: filterByEmail || undefined,
        filterByOrgName: filterByOrgName || undefined,
        filterByDivisionId: filterByDivisionId ? Number(filterByDivisionId) : undefined,
        filterByDepartmentId: filterByDepartmentId ? Number(filterByDepartmentId) : undefined,
        filterByLocationId: filterByLocationId ? Number(filterByLocationId) : undefined,
        filterByPhone: filterByPhone || undefined,
        requestedFrom: requestedFrom || undefined,
        requestedTo: requestedTo || undefined,
        modifiedFrom: modifiedFrom || undefined,
        modifiedTo: modifiedTo || undefined,
        approvedFrom: approvedFrom || undefined,
        approvedTo: approvedTo || undefined,
    })

    const [showConfirmationActiveUserModal, setShowConfirmationActiveUserModal] = useState(false)
    const [editData, setEditData] = useState({})
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [detailData, setDetailData] = useState<any>(null)
    const [showEditModal, setShowEditModal] = useState(false)
    const [editRequestData, setEditRequestData] = useState<any>(null)

    const handlePageSizeChange = (e: any) => {
        setPageSize(parseInt(e.target.value))
        setPage(1)
    }

    const handlePageClick = (e: any) => {
        setPage(e.selected + 1)
    }

    const handleSearchChange = (e: any) => {
        setSearchStr(e.target.value)
        setPage(1)
    }

    const handleSort = (property: string) => {
        setSortBy(property)
        if (sortedColumn === property) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortedColumn(property)
            setSortOrder('asc')
        }
    }

    useEffect(() => {
        refetch()
    }, [tabChange, showConfirmationActiveUserModal, showEditModal])

    useEffect(() => {
        setPage(1)
    }, [
        searchStr, filterByStatus, filterByCcRequest, filterByUserName, filterByEmail, filterByOrgName,
        filterByDivisionId, filterByDepartmentId, filterByLocationId, filterByPhone, requestedFrom, requestedTo,
        modifiedFrom, modifiedTo, approvedFrom, approvedTo,
    ])

    const handleTabChange = (status: string) => {
        setActiveTab(status)
        setFilterByStatus(status)
        setPage(1)
    }

    const handleRefresh = () => {
        refetch().then(() => {
            if (isSuccess) toast.success('Data Refresh successfully!')
        })
    }

    const clearFilters = () => {
        setFilterByCcRequest('0')
        setFilterByUserName('')
        setFilterByEmail('')
        setFilterByOrgName('')
        setFilterByDivisionId('')
        setFilterByDepartmentId('')
        setFilterByLocationId('')
        setFilterByPhone('')
        setRequestedFrom('')
        setRequestedTo('')
        setModifiedFrom('')
        setModifiedTo('')
        setApprovedFrom('')
        setApprovedTo('')
        setSearchStr('')
        setPage(1)
    }

    const openDetail = (row: any) => {
        setDetailData({ userDraftID: row?.userDraftID || row?.id })
        setShowDetailModal(true)
    }

    const openEdit = (row: any) => {
        setEditRequestData({ userDraftID: row?.userDraftID || row?.id })
        setShowEditModal(true)
    }

    const isColumnVisible = (columnId: string) => hiddenColumns.includes(columnId)

    const renderSortHeader = (property: string, label: string, className = '') => (
        <th className={className} onClick={() => handleSort(property)} style={{ cursor: 'pointer' }}>
            <div className='table-arrow'>
                {label}
                {sortedColumn === property ? (sortOrder === 'desc' ? <FaArrowDown /> : <FaArrowUp />) : ''}
            </div>
        </th>
    )

    const rangeValue = (from: string, to: string) =>
        from && to ? `${moment(from).format('MM/DD/YYYY')} - ${moment(to).format('MM/DD/YYYY')}` : 'MM/DD/YYYY'

    const columnToggles: { id: string; label: string }[] = [
        { id: 'sr', label: labelKey.sr },
        { id: 'firstName', label: labelKey.user },
        { id: 'managerEmail', label: 'Manager' },
        { id: 'cellPhone', label: 'Phone' },
        { id: 'division', label: 'Division' },
        { id: 'department', label: labelKey.department },
        { id: 'designation', label: 'Designation' },
        { id: 'dayforceId', label: 'Dayforce ID' },
        { id: 'findAddress', label: 'Find Address' },
        { id: 'location', label: 'Office Location' },
        { id: 'apiOrgName', label: labelKey.organizationName },
        { id: 'status', label: labelKey.status },
        { id: 'comments', label: labelKey.comments },
        { id: 'createdDT', label: 'Corp Requested Date' },
        { id: 'modifiedOn', label: 'Modified Date' },
        { id: 'approvelDT', label: 'Approved Date' },
        ...(isTravelModuleAccessAvailable ? [{ id: 'requestTravelPortalAccess', label: 'Travel Portal Access' }] : []),
        { id: 'requestCorporateCreaditCard', label: 'Corporate CC Requested' },
        { id: 'spendingLimit', label: 'Corp CC Limit' },
        { id: 'actions', label: labelKey.action },
    ]

    const visibleColCount = COLUMN_IDS.filter(id => isColumnVisible(id)).length || 1

    return (
        <>
            <UpdateUserStatusModal
                show={showConfirmationActiveUserModal}
                handleClose={() => setShowConfirmationActiveUserModal(false)}
                data={editData}
            />
            <OnboardingUserRequestDetailModal
                show={showDetailModal}
                handleClose={() => {
                    setShowDetailModal(false)
                    setDetailData(null)
                }}
                data={detailData}
            />
            <OnboardingUserRequestEditModal
                show={showEditModal}
                handleClose={() => {
                    setShowEditModal(false)
                    setEditRequestData(null)
                }}
                data={editRequestData}
                onSaved={() => refetch()}
            />
            <Tooltip id="refreshData" place="top" />
            <Tooltip id="viewDetail" place="top" />
            <Tooltip id="editRequest" place="top" />
            <Tooltip id="Approve-Status" place="top" />

            <div className={`card`}>
                <div className='card-header border-0 flex-column align-items-stretch pt-4'>
                    <Alert variant="primary" className="mb-3">
                        <p className='fs-6 mb-0'>
                            Onboarding user requests awaiting manager action. After approval, the user account is created. If a corporate credit card was requested, a CC request is opened and routed to higher management by division; otherwise higher management receives a registration notice.
                        </p>
                    </Alert>

                    <div className='d-flex flex-wrap gap-3 align-items-end mb-3'>
                        <div style={{ minWidth: 160 }}>
                            <TextField label='User Name' placeholder='Name' value={filterByUserName} onChange={(e: any) => setFilterByUserName(e.target.value)} />
                        </div>
                        <div style={{ minWidth: 180 }}>
                            <TextField label='Email' placeholder='Email' value={filterByEmail} onChange={(e: any) => setFilterByEmail(e.target.value)} />
                        </div>
                        <div style={{ minWidth: 140 }}>
                            <TextField label='Phone' placeholder='Phone' value={filterByPhone} onChange={(e: any) => setFilterByPhone(e.target.value)} />
                        </div>
                        <div style={{ minWidth: 160 }}>
                            <TextField label='Org Name' placeholder='Organization' value={filterByOrgName} onChange={(e: any) => setFilterByOrgName(e.target.value)} />
                        </div>
                        <div style={{ minWidth: 180 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>User Request Status:</label>
                            <SearchSelect
                                options={STATUS_OPTIONS}
                                value={STATUS_OPTIONS.find(o => o.value === String(filterByStatus)) || STATUS_OPTIONS[0]}
                                onChange={(e: any) => {
                                    const val = e?.value != null ? String(e.value) : ''
                                    setFilterByStatus(val)
                                    setActiveTab(val || constraintConfig.newUserRequestStatus.Pending)
                                    setPage(1)
                                }}
                                placeholder='All Statuses'
                                isClearable
                            />
                        </div>
                        <div style={{ minWidth: 180 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>Division:</label>
                            <SearchSelect
                                options={divisionOptions}
                                value={divisionOptions.find(o => o.value === filterByDivisionId) || divisionOptions[0]}
                                onChange={(e: any) => setFilterByDivisionId(e?.value != null ? String(e.value) : '')}
                                placeholder='All Divisions'
                                isClearable
                            />
                        </div>
                        <div style={{ minWidth: 180 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>Department:</label>
                            <SearchSelect
                                options={departmentOptions}
                                value={departmentOptions.find(o => o.value === filterByDepartmentId) || departmentOptions[0]}
                                onChange={(e: any) => setFilterByDepartmentId(e?.value != null ? String(e.value) : '')}
                                placeholder='All Departments'
                                isClearable
                            />
                        </div>
                        <div style={{ minWidth: 200 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>Office Location:</label>
                            <SearchSelect
                                options={locationOptions}
                                value={locationOptions.find(o => o.value === filterByLocationId) || locationOptions[0]}
                                onChange={(e: any) => setFilterByLocationId(e?.value != null ? String(e.value) : '')}
                                placeholder='All Locations'
                                isClearable
                            />
                        </div>
                        <div style={{ minWidth: 180 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>Corporate Credit Card Requested:</label>
                            <SearchSelect
                                options={CC_REQUEST_OPTIONS}
                                value={CC_REQUEST_OPTIONS.find(o => o.value === filterByCcRequest) || CC_REQUEST_OPTIONS[0]}
                                onChange={(e: any) => setFilterByCcRequest(e?.value != null ? String(e.value) : '0')}
                                placeholder='All'
                                isClearable
                            />
                        </div>
                        <div style={{ minWidth: 240 }}>
                            <DateRangeComp
                                label='Requested Date Range'
                                startDate={requestedFrom ? moment(requestedFrom) : moment().subtract(30, 'days')}
                                endDate={requestedTo ? moment(requestedTo) : moment()}
                                onApply={(_: any, picker: any) => {
                                    setRequestedFrom(moment(picker.startDate).format('YYYY-MM-DD'))
                                    setRequestedTo(moment(picker.endDate).format('YYYY-MM-DD'))
                                }}
                                onCancel={() => { setRequestedFrom(''); setRequestedTo('') }}
                                value={rangeValue(requestedFrom, requestedTo)}
                            />
                        </div>
                        <div style={{ minWidth: 240 }}>
                            <DateRangeComp
                                label='Modified Date Range'
                                startDate={modifiedFrom ? moment(modifiedFrom) : moment().subtract(30, 'days')}
                                endDate={modifiedTo ? moment(modifiedTo) : moment()}
                                onApply={(_: any, picker: any) => {
                                    setModifiedFrom(moment(picker.startDate).format('YYYY-MM-DD'))
                                    setModifiedTo(moment(picker.endDate).format('YYYY-MM-DD'))
                                }}
                                onCancel={() => { setModifiedFrom(''); setModifiedTo('') }}
                                value={rangeValue(modifiedFrom, modifiedTo)}
                            />
                        </div>
                        <div style={{ minWidth: 240 }}>
                            <DateRangeComp
                                label='Approved Date Range'
                                startDate={approvedFrom ? moment(approvedFrom) : moment().subtract(30, 'days')}
                                endDate={approvedTo ? moment(approvedTo) : moment()}
                                onApply={(_: any, picker: any) => {
                                    setApprovedFrom(moment(picker.startDate).format('YYYY-MM-DD'))
                                    setApprovedTo(moment(picker.endDate).format('YYYY-MM-DD'))
                                }}
                                onCancel={() => { setApprovedFrom(''); setApprovedTo('') }}
                                value={rangeValue(approvedFrom, approvedTo)}
                            />
                        </div>
                        <button type='button' className='btn btn-sm btn-light' onClick={clearFilters}>Clear</button>
                    </div>

                    <div className='d-flex flex-wrap gap-2 align-items-center justify-content-between'>
                        <div className='d-flex gap-1 align-items-center'>
                            <div className='btn-group'>
                                <button
                                    type='button'
                                    className={`btn btn-sm ${activeTab == constraintConfig.newUserRequestStatus.Pending ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.newUserRequestStatus.Pending)}
                                >
                                    {labelKey.pendingForApproval}
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm ${activeTab === constraintConfig.newUserRequestStatus.Approved ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.newUserRequestStatus.Approved)}
                                >
                                    Approved Users
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm ${activeTab === constraintConfig.newUserRequestStatus.Rejected ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.newUserRequestStatus.Rejected)}
                                >
                                    Rejected Users
                                </button>
                            </div>
                            <div
                                data-tooltip-id="refreshData" data-tooltip-content='Refresh Data'
                                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm ms-2'
                                onClick={handleRefresh}
                            >
                                <IoIosRepeat className='h-20px w-20px' />
                            </div>
                        </div>
                        <div className='card-title m-0 d-flex flex-wrap gap-2 align-items-center'>
                            <SearchBarComponent
                                className='me-2'
                                placeholder='User'
                                value={searchStr}
                                onChange={handleSearchChange}
                            />
                            {(roleID === constraintConfig.roleID.role2) && (
                                <Link to='/newuserrequest' className='btn btn-sm btn-primary'>
                                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                                    New User Request
                                </Link>
                            )}
                            {(roleID === constraintConfig.roleID.role4) && (
                                <Link to='/newuserrequestbymanager' className='btn btn-sm btn-primary'>
                                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                                    New User Request
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
                <div className='card-body py-3'>
                    <div className='table-responsive table-height'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                            <thead>
                                <tr className='fw-bold text-muted'>
                                    {isColumnVisible('sr') && <th className='rounded-start ps-4'>{labelKey.sr}</th>}
                                    {isColumnVisible('firstName') && renderSortHeader('firstName', labelKey.user)}
                                    {isColumnVisible('managerEmail') && <th>Manager</th>}
                                    {isColumnVisible('cellPhone') && <th>Phone</th>}
                                    {isColumnVisible('division') && <th>Division</th>}
                                    {isColumnVisible('department') && <th>{labelKey.department}</th>}
                                    {isColumnVisible('designation') && <th>Designation</th>}
                                    {isColumnVisible('dayforceId') && <th>Dayforce ID</th>}
                                    {isColumnVisible('findAddress') && <th>Find Address</th>}
                                    {isColumnVisible('location') && <th>Office Location</th>}
                                    {isColumnVisible('apiOrgName') && renderSortHeader('apiOrgName', labelKey.organizationName)}
                                    {isColumnVisible('status') && renderSortHeader('mgrActionStatus', 'User Status')}
                                    {isColumnVisible('comments') && <th>{labelKey.comments}</th>}
                                    {isColumnVisible('createdDT') && renderSortHeader('createdDT', 'Corp Requested Date')}
                                    {isColumnVisible('modifiedOn') && <th>Modified Date</th>}
                                    {isColumnVisible('approvelDT') && renderSortHeader('approvelDT', 'Approved Date')}
                                    {!!isTravelModuleAccessAvailable && isColumnVisible('requestTravelPortalAccess') && <th>Travel Portal Access</th>}
                                    {isColumnVisible('requestCorporateCreaditCard') && <th>Corporate CC Requested</th>}
                                    {isColumnVisible('spendingLimit') && <th>Corp CC Limit</th>}
                                    {isColumnVisible('actions') && (
                                        <th className='rounded-end text-end pe-4'>
                                            <TableSettingMenu>
                                                {columnToggles.map(col => (
                                                    <label key={col.id} className='form-check form-check-custom form-check-solid mb-2'>
                                                        <input
                                                            className='form-check-input'
                                                            type='checkbox'
                                                            checked={isColumnVisible(col.id)}
                                                            onChange={() => handleToggleColumn(col.id)}
                                                        />
                                                        <span className='form-check-label'>{col.label}</span>
                                                    </label>
                                                ))}
                                            </TableSettingMenu>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {!isLoading && (
                                    <>
                                        {data?.result?.data?.length > 0 ? (
                                            data?.result?.data?.map((row: any, index: any) => (
                                                <tr key={row.userDraftID || row.id || index}>
                                                    {isColumnVisible('sr') && (
                                                        <td className='ps-4'>{(page - 1) * pageSize + index + 1}</td>
                                                    )}
                                                    {isColumnVisible('firstName') && (
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                                                    <div className="symbol-label fs-3 bg-light-info text-info text-capitalize">{row?.firstName?.charAt(0)}</div>
                                                                </div>
                                                                <div className="d-flex flex-column">
                                                                    <span className="text-gray-800 fw-bold mb-1 text-capitalize">{row?.firstName} {row?.lastName}</span>
                                                                    <span className='text-lowercase'>{row?.userEmail}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    )}
                                                    {isColumnVisible('managerEmail') && <td>{displayManager(row)}</td>}
                                                    {isColumnVisible('cellPhone') && <td>{displayPhone(row)}</td>}
                                                    {isColumnVisible('division') && (
                                                        <td>{lookupOptionLabel(divisionOptions, row?.division?.id, row?.divisionId)}</td>
                                                    )}
                                                    {isColumnVisible('department') && (
                                                        <td>{lookupOptionLabel(departmentOptions, row?.department?.id, row?.departmentId)}</td>
                                                    )}
                                                    {isColumnVisible('designation') && (
                                                        <td>{lookupOptionLabel(designationOptions, row?.designation?.id, row?.designationId)}</td>
                                                    )}
                                                    {isColumnVisible('dayforceId') && <td>{row?.dayforceId || 'N/A'}</td>}
                                                    {isColumnVisible('findAddress') && (
                                                        <td className='text-wrap' style={{ maxWidth: 220 }}>{row?.findAddress || 'N/A'}</td>
                                                    )}
                                                    {isColumnVisible('location') && (
                                                        <td>{lookupOptionLabel(locationOptions, row?.location?.id, row?.locationId)}</td>
                                                    )}
                                                    {isColumnVisible('apiOrgName') && <td>{row?.apiOrgName}</td>}
                                                    {isColumnVisible('status') && (
                                                        <td>
                                                            {row?.userApprovalStatus?.id == constraintConfig.newUserRequestStatus.Approved ? (
                                                                <span className="badge badge-light-success fs-7 fw-semibold">{labelKey.approved}</span>
                                                            ) : row?.userApprovalStatus?.id == constraintConfig.newUserRequestStatus.Pending ? (
                                                                <span className="badge badge-light-info fs-7 fw-semibold">{labelKey.pendingForApproval}</span>
                                                            ) : (
                                                                <span className="badge badge-danger fs-7 fw-semibold">{labelKey.rejected}</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {isColumnVisible('comments') && (
                                                        <td className='text-wrap'>
                                                            {row?.comments ? (
                                                                <ReactReadMoreReadLess
                                                                    charLimit={30}
                                                                    readMoreText={"Read more"}
                                                                    readLessText={"Read less"}
                                                                    readMoreClassName="readMore"
                                                                    readLessClassName="readLess"
                                                                >
                                                                    {row.comments}
                                                                </ReactReadMoreReadLess>
                                                            ) : 'N/A'}
                                                        </td>
                                                    )}
                                                    {isColumnVisible('createdDT') && (
                                                        <td><DateTimeComp formattedDate={row?.createdDT} /></td>
                                                    )}
                                                    {isColumnVisible('modifiedOn') && (
                                                        <td><DateTimeComp formattedDate={row?.modifiedOn || row?.createdDT} /></td>
                                                    )}
                                                    {isColumnVisible('approvelDT') && (
                                                        <td><DateTimeComp formattedDate={row?.approvelDT} /></td>
                                                    )}
                                                    {!!isTravelModuleAccessAvailable && isColumnVisible('requestTravelPortalAccess') && (
                                                        <td>{row?.requestTravelPortalAccess ? 'Yes' : 'No'}</td>
                                                    )}
                                                    {isColumnVisible('requestCorporateCreaditCard') && (
                                                        <td className='text-center'>{row?.requestCorporateCreaditCard ? 'Yes' : 'No'}</td>
                                                    )}
                                                    {isColumnVisible('spendingLimit') && (
                                                        <td>
                                                            {row?.requestCorporateCreaditCard
                                                                ? (row?.spendingLimit != null && Number(row.spendingLimit) > 0
                                                                    ? <CurrencyComp amount={row.spendingLimit} />
                                                                    : 'N/A')
                                                                : 'N/A'}
                                                        </td>
                                                    )}
                                                    {isColumnVisible('actions') && (
                                                        <td>
                                                            <div className='d-flex gap-2 justify-content-end pe-2'>
                                                                <div
                                                                    data-tooltip-id="viewDetail"
                                                                    data-tooltip-content='View Detail'
                                                                    className='btn btn-bg-light btn-active-color-primary btn-sm'
                                                                    onClick={() => openDetail(row)}
                                                                >
                                                                    <FaEye className='h-14px w-14px' />
                                                                </div>
                                                                {isOrgAdmin && String(row?.userApprovalStatus?.id) === constraintConfig.newUserRequestStatus.Pending && (
                                                                    <div
                                                                        data-tooltip-id="editRequest"
                                                                        data-tooltip-content='Edit Request Details'
                                                                        className='btn btn-bg-light btn-active-color-primary btn-sm'
                                                                        onClick={() => openEdit(row)}
                                                                    >
                                                                        <FaPencilAlt className='h-14px w-14px' />
                                                                    </div>
                                                                )}
                                                                <div
                                                                    data-tooltip-id="Approve-Status"
                                                                    data-tooltip-content='Update User Status'
                                                                    className='btn btn-bg-light btn-active-color-primary btn-sm'
                                                                    onClick={() => {
                                                                        setEditData(row)
                                                                        setShowConfirmationActiveUserModal(true)
                                                                    }}
                                                                >
                                                                    <FaCheck className='h-14px w-14px' /> & <IoMdClose className='h-15px w-15px' />
                                                                </div>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={visibleColCount}>
                                                    <NoRecordFound />
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )}
                                {isLoading && (
                                    <tr>
                                        <td colSpan={visibleColCount} className="text-center">
                                            <LoadingComponent />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        totalResult={data?.result?.totalRecords === 0 ? 0 : ((data?.result?.pageNumber - 1) * data?.result?.pageSize) + 1}
                        toResult={data?.result?.totalRecords === 0 ? 0 : Math.min(data?.result?.pageNumber * data?.result?.pageSize, data?.result?.totalRecords)}
                        ofResult={data?.result?.totalRecords}
                        onChange={handlePageSizeChange}
                        pageSize={pageSize}
                        onPageChange={handlePageClick}
                        pageCount={data?.result?.totalPages || 0}
                    />
                </div>
            </div>
        </>
    )
}

export default AccountManager
